import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { buildBusinessGateCta } from "../../services/postAuthRouting";

const FONT = "inherit";
const ACCENT = "#7C3AED";
const ACCENT_DARK = "#6D28D9";
const ACCENT_SOFT = "#7C3AED14";
const ACCENT_SOFT_2 = "#7C3AED40";

const ICON = {
  lock: "M7 11V8a5 5 0 0110 0v3M5 11h14v9a1 1 0 01-1 1H6a1 1 0 01-1-1z",
  draft: "M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z",
  business: "M3 9l1.5-5h15L21 9M4 9h16v10a1 1 0 01-1 1H5a1 1 0 01-1-1zM3 9h18M9 13h6",
  bolt: "M13 2L4.5 13.5H11l-1 8.5L19.5 10H13z",
  chart: "M4 19V5M4 19h16M8 15l3-4 3 2 4-6",
  team: "M17 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M12.5 7a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
  arrow: "M5 12h14M13 6l6 6-6 6",
};

const PERKS = [
  { icon: ICON.bolt, label: "Launch campaigns, raffles & coupons" },
  { icon: ICON.chart, label: "Track reach, claims & sign-ups" },
  { icon: ICON.team, label: "Invite your team to collaborate" },
];

const CONTENT = {
  create: {
    badgeText: "Business required",
    badgeIcon: ICON.lock,
    badgeTone: "neutral",
    title: "Create your business",
    subtitle: (f) =>
      `${f} lives inside your business workspace. Set one up in the Business Hub — it only takes a minute.`,
    unlockLabel: "What you'll unlock",
    isDraft: false,
  },
  activate: {
    badgeText: "Draft business",
    badgeIcon: ICON.draft,
    badgeTone: "accent",
    title: "Activate your business",
    subtitle: (f) =>
      `Your business is still a draft. Finish setup in the Business Hub to unlock ${f}.`,
    unlockLabel: "Activating unlocks",
    isDraft: true,
  },
  upgrade: {
    badgeText: "Business feature",
    badgeIcon: ICON.lock,
    badgeTone: "neutral",
    title: "Become a business",
    subtitle: (f) =>
      `${f} is a business feature. Upgrade to a business account to unlock campaigns, offers, and analytics.`,
    unlockLabel: "What you'll unlock",
    isDraft: false,
  },
};

const CTA_LABEL = {
  create: "Create your business",
  activate: "Activate your business",
  upgrade: "Become a business",
};

function GhostPreview() {
  const tile = {
    background: "var(--card)",
    borderRadius: 16,
    padding: 20,
    height: 116,
    border: "1px solid var(--hairline)",
  };
  const bar = (w, h, bg, mt = 0) => (
    <div style={{ height: h, width: w, background: bg, borderRadius: 5, marginTop: mt }} />
  );
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        padding: "28px 34px",
        filter: "blur(3px) saturate(0.7)",
        opacity: 0.5,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <div style={{ maxWidth: 1160, margin: "0 auto" }}>
        {bar(150, 16, "var(--skel)")}
        <div style={{ marginTop: 12 }}>{bar(340, 30, "var(--skel-strong)")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, margin: "24px 0 20px" }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={tile}>
              {bar("60%", 12, "var(--skel-2)")}
              {bar("45%", 26, "var(--skel)", 16)}
              {bar("70%", 10, "var(--skel-3)", 14)}
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16 }}>
          <div style={{ ...tile, height: 280 }}>
            {bar(180, 14, "var(--skel-2)")}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 180, marginTop: 26 }}>
              {[55, 80, 65, 95, 72, 88].map((h, i) => (
                <div key={i} style={{ flex: 1, height: `${h}%`, background: "var(--skel-bar)", borderRadius: 8 }} />
              ))}
            </div>
          </div>
          <div style={{ ...tile, height: 280 }}>
            {bar(120, 14, "var(--skel-2)")}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 24 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ height: 44, background: "var(--skel-soft)", borderRadius: 10 }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Locked-state gate (design/Locked States.html) shown wherever a live business is required.
 * Copy + CTA adapt to why access is blocked: create a business, activate a draft, or upgrade an
 * individual account. `progress` (0–100) renders the draft completion ring when known.
 */
export default function BusinessRequiredState({
  reason = "create",
  featureLabel = "This workspace",
  nextPath = "/",
  progress = null,
}) {
  const content = CONTENT[reason] || CONTENT.create;
  const cta = buildBusinessGateCta(reason, nextPath);
  const ctaLabel = CTA_LABEL[reason] || CTA_LABEL.create;
  const showSecondary = String(nextPath || "/") !== "/";

  const badgeStyle =
    content.badgeTone === "accent"
      ? { background: ACCENT_SOFT, color: ACCENT }
      : { background: "var(--chip)", color: "var(--muted)" };

  const R = 37;
  const CIRC = 2 * Math.PI * R;
  const hasRing = content.isDraft && progress != null && Number.isFinite(Number(progress));
  const pct = Math.max(0, Math.min(100, Number(progress)));
  const ringOffset = CIRC * (1 - pct / 100);

  return (
    <div
      className="biz-gate"
      style={{ fontFamily: FONT, color: "var(--ink)", position: "relative", overflow: "hidden", minHeight: "calc(100vh - 96px)", width: "100%" }}
    >
      <style>{`
        .biz-gate {
          --card:#fff; --chip:#F4F4F5; --border:#EAEAEC; --hairline:#F0F0F2; --ink:#18181B; --muted:#71717A;
          --unlock-bg:#FBFBFC; --skel:#C7C7CE; --skel-strong:#B8B8C0; --skel-2:#D2D2D9; --skel-3:#E4E4EA;
          --skel-bar:#D8CEF0; --skel-soft:#F0EEF8; --scrim1:rgba(239,239,241,0.35); --scrim2:rgba(239,239,241,0.92);
        }
        body[data-theme='dark'] .biz-gate {
          --card:#171B24; --chip:#1F2530; --border:#262D3A; --hairline:#222A36; --ink:#F2F4F8; --muted:#A6B0C0;
          --unlock-bg:#1B2130; --skel:#2A3240; --skel-strong:#333B4A; --skel-2:#262D3A; --skel-3:#222A36;
          --skel-bar:#3A2F58; --skel-soft:#232036; --scrim1:rgba(15,18,24,0.35); --scrim2:rgba(15,18,24,0.94);
        }
        .biz-gate-cta:hover { background:${ACCENT_DARK} !important; }
        .biz-gate-secondary:hover { color:${ACCENT} !important; }
        @keyframes bizGateIn { from { opacity:0; transform:translateY(10px) scale(.985); } to { opacity:1; transform:none; } }
      `}</style>

      <GhostPreview />

      {/* soft scrim fading the preview into the gate */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(600px 420px at 50% 42%, var(--scrim1), var(--scrim2) 62%)",
        }}
      />

      {/* gate card */}
      <div style={{ position: "relative", zIndex: 2, minHeight: "calc(100vh - 96px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
        <div
          style={{
            width: "min(560px,100%)",
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 24,
            boxShadow: "0 24px 70px rgba(20,18,28,0.14)",
            padding: "40px 40px 34px",
            textAlign: "center",
            animation: "bizGateIn .4s cubic-bezier(.2,.7,.3,1) both",
          }}
        >
          {/* icon (+ optional draft ring / badge) */}
          <div style={{ position: "relative", width: 82, height: 82, margin: "0 auto 22px" }}>
            {hasRing ? (
              <svg width="82" height="82" viewBox="0 0 82 82" style={{ position: "absolute", inset: 0 }}>
                <circle cx="41" cy="41" r={R} fill="none" stroke={ACCENT_SOFT_2} strokeWidth="5" />
                <circle
                  cx="41"
                  cy="41"
                  r={R}
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={CIRC}
                  strokeDashoffset={ringOffset}
                  transform="rotate(-90 41 41)"
                />
              </svg>
            ) : null}
            <div
              style={{
                position: "absolute",
                inset: 12,
                borderRadius: 20,
                background: ACCENT_SOFT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: ACCENT,
              }}
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d={ICON.business} />
              </svg>
            </div>
            {hasRing ? (
              <span style={{ position: "absolute", bottom: -4, right: -4, height: 22, padding: "0 8px", display: "inline-flex", alignItems: "center", borderRadius: 20, background: ACCENT, color: "#fff", fontSize: 11, fontWeight: 800, boxShadow: "0 2px 6px rgba(0,0,0,.2)" }}>
                {pct}%
              </span>
            ) : null}
          </div>

          {/* badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 24, padding: "0 11px", borderRadius: 20, ...badgeStyle, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em", marginBottom: 14 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d={content.badgeIcon} />
            </svg>
            {content.badgeText}
          </div>

          <h1 style={{ margin: "0 0 10px", fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>{content.title}</h1>
          <p style={{ margin: "0 auto 24px", maxWidth: 400, color: "var(--muted)", fontSize: 14.5, lineHeight: 1.55 }}>
            {content.subtitle(featureLabel)}
          </p>

          {/* what unlocks */}
          <div style={{ textAlign: "left", background: "var(--unlock-bg)", border: "1px solid var(--hairline)", borderRadius: 15, padding: "16px 18px", marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 12 }}>
              {content.unlockLabel}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {PERKS.map((perk) => (
                <div key={perk.label} style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <span style={{ width: 26, height: 26, flex: "0 0 26px", borderRadius: 8, background: ACCENT_SOFT, color: ACCENT, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d={perk.icon} />
                    </svg>
                  </span>
                  <span style={{ fontSize: 13.5, color: "var(--ink)", fontWeight: 500 }}>{perk.label}</span>
                </div>
              ))}
            </div>
          </div>

          <RouterLink
            to={cta.path}
            className="biz-gate-cta"
            style={{
              width: "100%",
              height: 48,
              borderRadius: 13,
              background: ACCENT,
              color: "#fff",
              fontWeight: 700,
              fontSize: 14.5,
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              boxShadow: `0 6px 18px ${ACCENT_SOFT_2}`,
            }}
          >
            {ctaLabel}
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d={ICON.arrow} />
            </svg>
          </RouterLink>

          {showSecondary ? (
            <RouterLink
              to="/"
              className="biz-gate-secondary"
              style={{ display: "inline-block", marginTop: 12, color: "var(--muted)", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
            >
              Back to Studio home
            </RouterLink>
          ) : null}
        </div>
      </div>
    </div>
  );
}
